import React, { useCallback, useContext, useMemo, useState } from 'react';

import { ValleyBankLoanAccountWithCollateralAndStats } from '@finley-technologies/valley-bank-utils';

// Helpers
const areStringSetsEqual = (
  setA: Set<string> | null,
  setB: Set<string> | null,
) => {
  if (!setA && !setB) return true;
  if (!setA || !setB) return false;
  if (setA.size !== setB.size) return false;
  for (const value of setA) {
    if (!setB.has(value)) return false;
  }
  return true;
};

// Types
type ContextValue = {
  queuedSelectedCollateralAccounts: Set<string> | null;
  setQueuedSelectedCollateralAccounts: (next: Set<string> | null) => void;
  queuedSelectedFinleyReceivableIds: Set<string> | null;
  setQueuedSelectedFinleyReceivableIds: (next: Set<string> | null) => void;
  selectedCollateralAccounts: Set<string> | null;
  setSelectedCollateralAccounts: (next: Set<string> | null) => void;
  selectedFinleyReceivableIds: Set<string> | null;
  setSelectedFinleyReceivableIds: (next: Set<string> | null) => void;
  isFilterApplied: boolean;
  setIsFilterApplied: (next: boolean) => void;
  setInitialSelections: (
    loanAccount: ValleyBankLoanAccountWithCollateralAndStats | null,
  ) => void;
  getDisplayRefetchAlert: (props: {
    loanAccount: ValleyBankLoanAccountWithCollateralAndStats | null;
    isLoading: boolean;
  }) => boolean;
};

// Contexts
const CollateralAccountsSelectorContext = React.createContext<ContextValue>({
  queuedSelectedCollateralAccounts: null,
  setQueuedSelectedCollateralAccounts: () => {},
  queuedSelectedFinleyReceivableIds: null,
  setQueuedSelectedFinleyReceivableIds: () => {},
  selectedCollateralAccounts: null,
  setSelectedCollateralAccounts: () => {},
  selectedFinleyReceivableIds: null,
  setSelectedFinleyReceivableIds: () => {},
  isFilterApplied: false,
  setIsFilterApplied: () => {},
  setInitialSelections: () => {},
  getDisplayRefetchAlert: () => false,
});

// Exports
type Props = { children: React.ReactNode };
export const CollateralAccountsSelectorProvider = ({ children }: Props) => {
  // !TODO @eldarshamukhamedov This should really be a reducer
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [
    queuedSelectedCollateralAccounts,
    setQueuedSelectedCollateralAccounts,
  ] = useState<Set<string> | null>(null);
  const [
    queuedSelectedFinleyReceivableIds,
    setQueuedSelectedFinleyReceivableIds,
  ] = useState<Set<string> | null>(null);
  const [
    selectedCollateralAccounts,
    setSelectedCollateralAccounts,
  ] = useState<Set<string> | null>(null);
  const [
    selectedFinleyReceivableIds,
    setSelectedFinleyReceivableIds,
  ] = useState<Set<string> | null>(null);

  const setInitialSelections = useCallback(
    (loanAccount: ValleyBankLoanAccountWithCollateralAndStats | null) => {
      const accountNumberSet = new Set<string>();
      const finleyReceivableIdsSet = new Set<string>();

      if (
        loanAccount &&
        (!queuedSelectedCollateralAccounts ||
          !queuedSelectedFinleyReceivableIds ||
          !selectedCollateralAccounts ||
          !selectedFinleyReceivableIds)
      ) {
        loanAccount.collateralAccounts.forEach((collateralAccount) => {
          accountNumberSet.add(collateralAccount.accountNumber);

          collateralAccount.receivables.forEach((receivable) => {
            finleyReceivableIdsSet.add(receivable.finley_receivable_id);
          });
        });

        setQueuedSelectedCollateralAccounts(accountNumberSet);
        setSelectedCollateralAccounts(accountNumberSet);
        setQueuedSelectedFinleyReceivableIds(finleyReceivableIdsSet);
        setSelectedFinleyReceivableIds(finleyReceivableIdsSet);
      }
    },
    [
      selectedCollateralAccounts,
      selectedFinleyReceivableIds,
      queuedSelectedCollateralAccounts,
      queuedSelectedFinleyReceivableIds,
    ],
  );

  const getDisplayRefetchAlert = useCallback(
    ({
      loanAccount,
      isLoading,
    }: {
      loanAccount: ValleyBankLoanAccountWithCollateralAndStats | null;
      isLoading: boolean;
    }) => {
      if (isLoading || !loanAccount) {
        return false;
      }

      const areCollateralAccountsEqual = areStringSetsEqual(
        selectedCollateralAccounts,
        queuedSelectedCollateralAccounts,
      );

      const areReceivableIdsEqual = areStringSetsEqual(
        selectedFinleyReceivableIds,
        queuedSelectedFinleyReceivableIds,
      );

      return !areCollateralAccountsEqual || !areReceivableIdsEqual;
    },
    [
      selectedCollateralAccounts,
      selectedFinleyReceivableIds,
      queuedSelectedCollateralAccounts,
      queuedSelectedFinleyReceivableIds,
    ],
  );

  const contextValue = useMemo(() => {
    return {
      queuedSelectedCollateralAccounts,
      setQueuedSelectedCollateralAccounts,
      queuedSelectedFinleyReceivableIds,
      setQueuedSelectedFinleyReceivableIds,
      selectedCollateralAccounts,
      setSelectedCollateralAccounts,
      selectedFinleyReceivableIds,
      setSelectedFinleyReceivableIds,
      isFilterApplied,
      setIsFilterApplied,
      setInitialSelections,
      getDisplayRefetchAlert,
    };
  }, [
    getDisplayRefetchAlert,
    isFilterApplied,
    queuedSelectedCollateralAccounts,
    queuedSelectedFinleyReceivableIds,
    selectedCollateralAccounts,
    selectedFinleyReceivableIds,
    setInitialSelections,
  ]);
  return (
    <CollateralAccountsSelectorContext.Provider value={contextValue}>
      {children}
    </CollateralAccountsSelectorContext.Provider>
  );
};

export const useCollateralAccountsSelector = () =>
  useContext(CollateralAccountsSelectorContext);
